import {
    createSearchParams,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import {
    Container,
    Fab,
    ImageList,
    ImageListItem,
    Skeleton,
    Tooltip,
    Typography,
} from '@mui/material'
import { useEffect } from 'react'
import { Ear } from '../../../../../components/Ear'
import { useInnsynContext } from '../../../../../context/InnsynContextProvider'
import {
    INNSYN_SEARCH_OEREMERKE_PATH,
    INNSYN_SEARCH_OEREMERKE_SNITT_COMBINATION_PATH,
} from '../../../../../paths'
import { FormattedMessage } from 'react-intl'
import NavigationHeader from '../../../../../components/NavigationHeader'
import colors from '../../../../../styles/colors.scss'
import '../selectCut.scss'

export const SelectBaseCut = () => {
    const navigate = useNavigate()
    const { state } = useLocation() as RightEar
    const [query] = useSearchParams()
    const cut_pos = query.get('cut_pos')
    const { createAlert, clearAlert, merker, snitt, isLoadingSnitt } =
        useInnsynContext()
    const areaStringArray = query.getAll('a')
    const districtStringArray = query.getAll('d')
    const siidaStringArray = query.getAll('s')

    useEffect(() => {
        if (areaStringArray.length <= 0 && siidaStringArray.length <= 0) {
            createAlert({
                body: <FormattedMessage id='select-cut-alert-limit-results' />,
                severity: 'info',
                uniqueId: 'select-area-or-district',
                displayInline: true,
            })
        }
        return () => {
            clearAlert('select-area-or-district')
        }
    }, [])

    const hasNoAreaDistrictOrSiidaSelected = () => {
        return (
            areaStringArray.length === 0 &&
            districtStringArray.length === 0 &&
            siidaStringArray.length === 0
        )
    }

    const getBaseCutsForEarPosition = () => {
        const filteredMerkesakDataSetForCurrentPositionAndGeography =
            merker
                ?.filter(
                    (el) =>
                        areaStringArray.length === 0 ||
                        areaStringArray.some(
                            (areaString) =>
                                areaString === el.omraadeId.toString() ||
                                siidaStringArray.some(
                                    (siidaString) =>
                                        siidaString === el.siidaId?.toString()
                                )
                        )
                )
                .filter(
                    (el) =>
                        districtStringArray.length === 0 ||
                        districtStringArray.some(
                            (districtString) =>
                                districtString === el.distriktId?.toString() ||
                                siidaStringArray.some(
                                    (siidaString) =>
                                        siidaString === el.siidaId?.toString()
                                )
                        )
                )
                .filter(
                    (el) =>
                        siidaStringArray.length === 0 ||
                        siidaStringArray.some(
                            (siidaString) =>
                                siidaString === el.siidaId?.toString()
                        )
                )
                .map((el) => {
                    const pos_key = `snittPosisjon${cut_pos}` as keyof Merke
                    return {
                        snittIdForCurrentPosition: el[pos_key],
                        ...el,
                    } as ExtendedMerke
                }) || []
        return (
            snitt
                ?.filter((cut) => cut_pos && cut.posisjon === parseInt(cut_pos))
                .filter(
                    (cut) =>
                        hasNoAreaDistrictOrSiidaSelected() ||
                        filteredMerkesakDataSetForCurrentPositionAndGeography.find(
                            (merkeSak) => {
                                return (
                                    merkeSak.snittIdForCurrentPosition ===
                                    cut.bokstavkombinasjon
                                )
                            }
                        )
                ) || []
        )
    }

    const validPosition = (
        currentPosition: number,
        possiblePosition: number
    ) => {
        if (
            (currentPosition === 1 || currentPosition === 3) &&
            (possiblePosition === 1 || possiblePosition === 3)
        ) {
            return true
        } else if (
            (currentPosition === 4 || currentPosition === 6) &&
            (possiblePosition === 4 || possiblePosition === 6)
        ) {
            return true
        } else if (
            (currentPosition === 2 && possiblePosition === 2) ||
            (currentPosition === 5 && possiblePosition === 5)
        ) {
            return true
        }
        return false
    }

    const getBaseCutLetters = (snittene: Snitt[]) => {
        let letters = [] as string[]
        snittene.forEach((snitt) => {
            for (var i = 0; i < snitt.bokstavkombinasjon.length; i++) {
                if (!letters.includes(snitt.bokstavkombinasjon[i])) {
                    letters.push(snitt.bokstavkombinasjon[i])
                }
            }
        })
        return (
            snitt?.filter((cut) => {
                return (
                    cut_pos &&
                    letters.includes(cut.bokstavkombinasjon) &&
                    validPosition(parseInt(cut_pos), cut.posisjon)
                )
            }) || []
        )
    }

    const sortSnittByChapterAndPosition = (s1: Snitt, s2: Snitt) => {
        if (
            cut_pos &&
            s1.posisjon === parseInt(cut_pos) &&
            s2.posisjon !== parseInt(cut_pos)
        ) {
            return -1
        } else if (
            cut_pos &&
            s2.posisjon === parseInt(cut_pos) &&
            s1.posisjon !== parseInt(cut_pos)
        ) {
            return 1
        }
        if (s1.kapittel === null && s2.kapittel === null) {
            return s1.bokstavkombinasjon.localeCompare(s2.bokstavkombinasjon)
        } else if (s1.kapittel === null) {
            return 1
        } else if (s2.kapittel === null) {
            return -1
        } else if (s1.kapittel === s2.kapittel) {
            return s1.bokstavkombinasjon.localeCompare(s2.bokstavkombinasjon)
        } else {
            return s1?.kapittel - s2?.kapittel
        }
    }

    return (
        <Container sx={{ height: '100%' }}>
            <NavigationHeader
                onClickBack={() =>
                    navigate(
                        {
                            pathname: INNSYN_SEARCH_OEREMERKE_PATH,
                            search: `?${query}`,
                        },
                        { state: { isRightEar: state?.isRightEar } }
                    )
                }
            />
            <Typography
                sx={{
                    fontSize: '1.25rem',
                    marginTop: '1.25rem',
                    marginBottom: '1.75rem',
                }}>
                <Fab
                    sx={{
                        marginRight: '1rem',
                        width: '2.25rem',
                        height: '1rem',
                        backgroundColor: 'white',
                        outline: `${colors.blue} solid 1px`,
                        boxShadow: 'none',
                    }}>
                    <div style={{ fontSize: '1.25rem', color: colors.blue }}>
                        1
                    </div>
                </Fab>
                <FormattedMessage id={'search-page.choose-cut-2'} />
            </Typography>
            {isLoadingSnitt && (
                <ImageList cols={2} gap={12}>
                    <Skeleton animation='wave' height='300px' />
                    <Skeleton animation='wave' height='300px' />
                    <Skeleton animation='wave' height='300px' />
                    <Skeleton animation='wave' height='300px' />
                    <Skeleton animation='wave' height='300px' />
                    <Skeleton animation='wave' height='300px' />
                    <Skeleton animation='wave' height='300px' />
                    <Skeleton animation='wave' height='300px' />
                </ImageList>
            )}
            {!isLoadingSnitt && (
                <ImageList
                    sx={{ width: '100%', height: '100%' }}
                    cols={2}
                    gap={12}>
                    {getBaseCutLetters(getBaseCutsForEarPosition())
                        .sort(sortSnittByChapterAndPosition)
                        .filter((cut, i, self) => {
                            return (
                                self.findIndex(
                                    (cut2) =>
                                        cut2.bokstavkombinasjon ===
                                        cut.bokstavkombinasjon
                                ) === i
                            )
                        })
                        .map((cut) => (
                            <Tooltip
                                arrow
                                title={`bokstavkomb ${cut.bokstavkombinasjon} id ${cut.id} pos ${cut.posisjon} kap ${cut.kapittel}`}
                                placement='top'>
                                <ImageListItem key={cut.id}>
                                    <div
                                        className='chosenCut'
                                        id={cut.id.toString()}
                                        style={{ paddingLeft: '0.25rem' }}
                                        aria-label={`basissnitt ${cut.bokstavkombinasjon}`}
                                        onClick={() => {
                                            query.set(
                                                'selected_base_cut',
                                                cut.id.toString()
                                            )
                                            navigate({
                                                pathname:
                                                    INNSYN_SEARCH_OEREMERKE_SNITT_COMBINATION_PATH,
                                                search: `?${createSearchParams(
                                                    query
                                                )}`,
                                            })
                                        }}>
                                        <Ear
                                            cuts={[cut]}
                                            show={
                                                cut_pos &&
                                                (parseInt(cut_pos) === 1 ||
                                                    parseInt(cut_pos) === 2 ||
                                                    parseInt(cut_pos) === 3)
                                                    ? 'right'
                                                    : 'left'
                                            }
                                        />
                                    </div>
                                </ImageListItem>
                            </Tooltip>
                        ))}
                </ImageList>
            )}
        </Container>
    )
}

export default SelectBaseCut
